type ReplacementMap = {
  [key: string]: number | ReplacementMap | string | undefined;
};

export const commafyInteger = (value: number) => {
  let integer = Math.round(value);
  let prefix = '';
  if (integer < 0) {
    integer = -integer;
    prefix = '-';
  }

  const segments = String(integer)
    .split('') // break the number up into its digits
    .reverse() // work from the smallest to largest place
    .reduce(
      ([segment, ...segments], digit) => {
        if (segment.length < 3) {
          // add to the leading segment
          return [`${digit}${segment}`, ...segments];
        }

        // start a new segment
        return [`${digit}`, segment, ...segments];
      },
      [''],
    );

  return `${prefix}${segments.join()}`;
};

const replacementsPattern = /{[\w\.]+}/g;
export const findReplacementKeys = (text: string): string[] => {
  const matches = text.match(replacementsPattern);
  if (!matches) {
    return [];
  }

  const matchesSet = new Set<string>(matches);
  const keys: string[] = [];
  for (const match of matchesSet.values()) {
    keys.push(match.substr(1, match.length - 2));
  }

  return keys;
};

export const formatText = (text: string, replacements: ReplacementMap) => {
  if (typeof text !== 'string') {
    throw new Error(
      `FLMPiano.formatText expects the first argument to be a string, received: ${JSON.stringify(
        text,
      )}`,
    );
  }

  if (typeof replacements !== 'object') {
    throw new Error(
      `FLMPiano.formatText expects the second argument to be an object, received: ${JSON.stringify(
        replacements,
      )}`,
    );
  }

  const replacementKeys = findReplacementKeys(text);

  return replacementKeys.reduce(
    (output, key) => replaceKey(output, key, replacements),
    text,
  );
};

export const getReplacement = (
  key: string,
  replacements: ReplacementMap,
): string | undefined => {
  const [initialKey, ...rest] = key.split('.');
  if (rest.length === 0) {
    const value = replacements[initialKey];

    switch (typeof value) {
      case 'string':
        return value;

      case 'boolean':
      case 'number':
        return String(value);

      case 'object': // can't reliably stringify an object for end users
      default:
        return undefined;
    }
  } else {
    const nestedReplacements = replacements[initialKey];
    if (typeof nestedReplacements === 'object') {
      return getReplacement(rest.join('.'), nestedReplacements);
    }
  }

  return undefined;
};

export const pluralize = (
  word?: string,
  count?: number,
  pluralWord?: string,
): string => {
  if (typeof word !== 'string') {
    throw new Error(
      `FLMPiano.pluralize expects the first argument to be a string, received: ${JSON.stringify(
        word,
      )}`,
    );
  }

  if (typeof count !== 'number' || isNaN(count)) {
    throw new Error(
      `FLMPiano.pluralize expects the second argument to be a string, received: ${JSON.stringify(
        count,
      )}`,
    );
  }

  if (!['string', 'undefined'].includes(typeof pluralWord)) {
    throw new Error(
      `FLMPiano.pluralize expects the third argument to be a string | undefined, received: ${JSON.stringify(
        pluralWord,
      )}`,
    );
  }

  if (count === 1) {
    return word;
  }

  return pluralWord || `${word}s`;
};

export const replaceKey = (
  text: string,
  key: string,
  replacements: ReplacementMap,
) => {
  const replacementValue = getReplacement(key, replacements);
  if (typeof replacementValue === 'undefined') {
    return text;
  }

  return text.replace(new RegExp(`{${key}}`, 'g'), replacementValue);
};
